import { z } from 'zod';
import { ecrireJson, lireJson } from './stockage';

/** The translations launched and not yet seen to their end, in `sessionStorage`. */
export const CLE_SUIVI = 'rosetta.suivi';

/** The followed translations are read again at this pace once the user left their waiting screen. */
export const INTERVALLE_SUIVI_MS = 5000;

const entreeSuiviSchema = z.object({
  /** Identifier returned by `lancerTraduction`. */
  id: z.string().min(1),
  /** The title the translation was sent with; empty when it has none. */
  titre: z.string(),
  /** Who launched it: a translation of another account is never announced to this one. */
  utilisateurId: z.string().min(1),
});
export type EntreeSuivi = z.infer<typeof entreeSuiviSchema>;

const suiviSchema = z.array(entreeSuiviSchema);

// The translations a screen of their own is waiting for right now (the waiting screen W1 and the
// result, #7). In memory only: a screen that is gone (reload, other page) no longer waits, and
// the follow-up takes over.
let attendues: ReadonlySet<string> = new Set();

type Ecouteur = () => void;
const ecouteurs = new Set<Ecouteur>();

function notifier(): void {
  for (const ecouteur of ecouteurs) ecouteur();
}

// `useSyncExternalStore` wants the same array until the content changes: the last read is
// kept with the raw string it came from.
let dernierBrut: string | null = null;
let dernierSuivi: readonly EntreeSuivi[] = [];

/** The followed translations, oldest first; the same array as long as nothing changed. */
export function lireSuivi(): readonly EntreeSuivi[] {
  const brut = window.sessionStorage.getItem(CLE_SUIVI);
  if (brut === dernierBrut) return dernierSuivi;
  dernierSuivi = lireJson(window.sessionStorage, CLE_SUIVI, suiviSchema) ?? [];
  // `lireJson` drops an invalid entry: the raw value to remember is what is left.
  dernierBrut = window.sessionStorage.getItem(CLE_SUIVI);
  return dernierSuivi;
}

function ecrireSuivi(suivi: readonly EntreeSuivi[]): void {
  if (suivi.length === 0) window.sessionStorage.removeItem(CLE_SUIVI);
  else ecrireJson(window.sessionStorage, CLE_SUIVI, suivi);
  notifier();
}

/**
 * Starts following a translation (the input screen does it as soon as it is launched).
 * `attendue` says the screen that launched it hands it to its own waiting screen (W1, #7): it
 * is then marked as awaited BEFORE it enters the list, so the follow-up never reads it in
 * between, and the mark stays until that screen stops waiting (`attendre`).
 */
export function inscrireSuivi(entree: EntreeSuivi, { attendue = false } = {}): void {
  if (attendue) attendues = new Set([...attendues, entree.id]);
  const autres = lireSuivi().filter((courante) => courante.id !== entree.id);
  ecrireSuivi([...autres, entree]);
}

/** Stops following a translation: it was seen to its end, or it cannot be read any more. */
export function retirerSuivi(id: string): void {
  const suivi = lireSuivi();
  if (suivi.some((entree) => entree.id === id)) {
    ecrireSuivi(suivi.filter((entree) => entree.id !== id));
  }
}

/** Calls `ecouteur` at every change of the followed translations or of the awaited ones. */
export function abonnerSuivi(ecouteur: Ecouteur): () => void {
  ecouteurs.add(ecouteur);
  return () => {
    ecouteurs.delete(ecouteur);
  };
}

/**
 * A screen of the translation `id` (the waiting screen W1, the result) says it waits for it:
 * nothing else announces that translation (the screen shows the outcome itself, with no
 * toast). Returns the way to stop waiting, which the screen calls when it goes away; the
 * follow-up then takes the translation over (if the screen has not removed it from the list
 * by then).
 */
export function attendre(id: string): () => void {
  attendues = new Set([...attendues, id]);
  notifier();
  return () => {
    attendues = new Set([...attendues].filter((courante) => courante !== id));
    notifier();
  };
}

/** The translations an input screen waits for, as the same set until it changes. */
export function lireAttendues(): ReadonlySet<string> {
  return attendues;
}
